import { RootStore } from "app/rootStore";
import { forEach } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { refreshAccessToken } from "shared/api/auth/tokenApi";
import { setAuthHeader } from "shared/api/utils";
import apiConstants from "shared/constants/apiConstants";
import { COMMA_SEPARATOR, NO_ACCESS_TOKEN_MESSAGE, ACCOUNT_TYPE_NAMES } from "shared/constants/appConstants";
import {
    ACCESS_TOKEN,
    REFRESH_TOKEN,
    REFRESH_TOKEN_RETRY_COUNT,
    REFRESH_TOKEN_RETRY_DELAY,
} from "shared/constants/authConstants";
import { AccountType, UserStatus } from "shared/constants/enums";
import { LocalizeFunction } from "shared/hooks/useLocalize";
import { AdditionalDetails } from "shared/interfaces/myProfile";
import { UserRoles } from "shared/interfaces/rolePermission";
import AuthResponse from "shared/models/authResponse";
import { IsPrivacyPolicyConfirmed, PrivacyPolicyConfirmed } from "shared/store/models/privacyPolicyConfirmed";
import { getAccountTypeId, parseJwt } from "shared/utils/jwt.utils";
import { retry } from "shared/utils/promise.utils";
import { CompanyUserPermissions, InternalUserPermissions, OmeUserPermissions } from "shared/constants/permissions";

const clearAuthDataFromLocalStorage = (): void => {
    forEach([REFRESH_TOKEN, ACCESS_TOKEN], value => window.localStorage.removeItem(value));
};

const saveAuthDataToLocalStorage = (authResponse: AuthResponse): void => {
    window.localStorage.setItem(ACCESS_TOKEN, authResponse.accessToken);
    window.localStorage.setItem(REFRESH_TOKEN, authResponse.refreshToken);
};

export class AuthStore {
    @observable public userId?: number;
    @observable public accountTypeId?: AccountType;
    @observable public accountTypeIds?: Array<AccountType>;
    @observable public status?: UserStatus;
    @observable public companyId?: number;
    @observable public additionalDetails?: AdditionalDetails;
    @observable public isPrivacyPolicyConfirmed?: PrivacyPolicyConfirmed;
    @observable public requiresMissingDetails?: boolean;

    @observable private permissions: UserRoles = new Set();
    @observable private accessToken?: string;

    private refreshToken?: string;
    private readonly rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
        this.initFromLocalStorage();
        window.addEventListener("storage", this.updateLocalStorageHandler, false);
    }

    @action.bound
    public initFromLocalStorage(): void {
        const accessToken = window.localStorage.getItem(ACCESS_TOKEN);
        const refreshToken = window.localStorage.getItem(REFRESH_TOKEN);

        if (!accessToken || !refreshToken) {
            return;
        }

        this.setAuthData({ accessToken, refreshToken });
    }

    @action.bound
    public setAuthData(authData: AuthResponse): void {
        this.setAccessToken(authData.accessToken);
        this.refreshToken = authData.refreshToken;

        if (!this.isDormant) {
            saveAuthDataToLocalStorage(authData);
        }
    }

    @action.bound
    public updateLocalStorageHandler(storageEvent: StorageEvent): void {
        const newValue = storageEvent.newValue || undefined;

        switch (storageEvent.key) {
            case ACCESS_TOKEN:
                this.setAccessToken(newValue);
                break;
            default:
                break;
        }
    }

    @action.bound
    public logout(): void {
        this.setAccessToken(undefined);
        this.rootStore.myDepositStore.logout();
        this.rootStore.appNotificationsStore.stopFetchNotReadCount();
        this.rootStore.appNotificationsStore.stopFetchNotifications();
    }

    @action.bound
    public async refreshAccessToken(): Promise<void> {
        if (!this.accessToken) {
            // eslint-disable-next-line @typescript-eslint/no-throw-literal
            throw NO_ACCESS_TOKEN_MESSAGE;
        }

        try {
            const response = await retry(
                () => {
                    const refreshTokenToSend = this.isDormant
                        ? this.refreshToken
                        : window.localStorage.getItem(REFRESH_TOKEN);

                    return refreshAccessToken(refreshTokenToSend!);
                },
                REFRESH_TOKEN_RETRY_DELAY,
                REFRESH_TOKEN_RETRY_COUNT
            );

            this.setAuthData(response);
        } catch (error) {
            if (error.response && error.response.status === apiConstants.STATUS_CODES.BAD_REQUEST) {
                this.logout();
            }

            throw error;
        }
    }

    @computed get fullName(): string {
        return `${this.additionalDetails?.firstName} ${this.additionalDetails?.lastName}`.trim();
    }

    @computed get isAuthenticated(): boolean {
        return !!this.accessToken && this.status === UserStatus.Active;
    }

    @computed get isDormant(): boolean {
        return !!this.accessToken && this.status === UserStatus.Dormant;
    }

    @computed get isPrivacyPoliciesConfirmed(): boolean {
        return this.isPrivacyPolicyConfirmed === IsPrivacyPolicyConfirmed.Yes;
    }

    @computed get userPermissions(): Set<InternalUserPermissions | CompanyUserPermissions | OmeUserPermissions> {
        return this.permissions;
    }

    public getAccountTypeLabel(localize: LocalizeFunction): string {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.accountTypeIds.map(role => localize(ACCOUNT_TYPE_NAMES[role])).join(", ");
    }

    @action.bound
    private refetchMenuItemsOnPermissionsChanged(draftPermissions: string, newPermissions: string) {
        if (newPermissions.length && draftPermissions !== newPermissions) {
            if (this.rootStore && this.rootStore.layoutStore && this.requiresMissingDetails) {
                this.rootStore.layoutStore.fetchMenuItems();
            }
        }
    }

    @action.bound
    private setAccessToken(accessToken?: string) {
        if (accessToken) {
            const draftPermissions = [...this.permissions].join(COMMA_SEPARATOR);
            const parsedJwtToken = parseJwt(accessToken);
            this.accessToken = accessToken;
            this.userId = Number(parsedJwtToken.id);
            this.accountTypeId = getAccountTypeId(parsedJwtToken.accountTypeIds);
            this.accountTypeIds = parsedJwtToken.accountTypeIds.split(COMMA_SEPARATOR).map(roleId => Number(roleId));
            this.status = Number(parsedJwtToken.statusId);
            this.companyId = Number(parsedJwtToken.companyId);
            this.additionalDetails = parsedJwtToken.additionalDetails
                ? JSON.parse(parsedJwtToken.additionalDetails)
                : undefined;
            this.isPrivacyPolicyConfirmed = parsedJwtToken.isPrivacyPolicyAndTermsAndConditionsConfirmed;
            this.permissions = new Set(
                parsedJwtToken.permissions.split(COMMA_SEPARATOR).map(permission => Number(permission))
            );
            setAuthHeader(this.accessToken);
            this.rootStore.setAccountTypeIds = parsedJwtToken.accountTypeIds;
            this.refetchMenuItemsOnPermissionsChanged(draftPermissions, parsedJwtToken.permissions);
            this.requiresMissingDetails = parsedJwtToken.requiresMissingDetails === "True";
        } else {
            this.accessToken = undefined;
            this.refreshToken = undefined;
            this.userId = undefined;
            this.accountTypeId = undefined;
            this.accountTypeIds = [];
            this.companyId = undefined;
            this.permissions = new Set();
            this.status = undefined;
            this.additionalDetails = undefined;
            this.isPrivacyPolicyConfirmed = IsPrivacyPolicyConfirmed.Yes;
            this.requiresMissingDetails = undefined;
            clearAuthDataFromLocalStorage();
            setAuthHeader(undefined);
        }
    }
}
